import { G } from '@mobily/ts-belt';
import { Box, DialogContent, Stack, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useDataProvider, useTranslate } from 'react-admin';

import {
  GetB2bEmployeesBulkUploadErrorsOneResponse,
  GetB2bEmployeesBulkUploadErrorsResponse,
  GetB2bEmployeesBulkUploadResultsOneResponse,
} from '@Common/types/bo/b2b/employees/bulk_upload';

import { DialogTitleWithX } from '@sh4res/bo-common/components';
import { BulkUploadErrorsList } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/components/BulkUploadErrorsList';
import { BulkUploadProgressBar } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/components/BulkUploadProgressBar';
import { BulkUploadStatusBadge } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/components/BulkUploadStatusBadge';
import { BulkUploadStatusLegend } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/components/BulkUploadStatusLegend';
import { computeStatuses } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/components/bulkUploadStatus';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';

const REFRESH_TIMER = 2000;

interface ResultsProps {
  bulkUploadResultId: number;
  close: () => void;
}

export function Results({ bulkUploadResultId, close }: ResultsProps) {
  const translate = useTranslate();
  const resourceBasePath = useResourceBasePath();
  const dataProvider = useDataProvider();

  const [bulkUploadResult, setBulkUploadResult] =
    useState<GetB2bEmployeesBulkUploadResultsOneResponse | null>(null);

  const [bulkUploadErrors, setBulkUploadErrors] =
    useState<GetB2bEmployeesBulkUploadErrorsResponse | null>(null);

  const getBulkUpload = useCallback(async () => {
    const { data: b2bEmployeesBulkUploadResult } =
      await dataProvider.getOne<GetB2bEmployeesBulkUploadResultsOneResponse>(
        `${resourceBasePath}employees/bulk_upload/results`,
        {
          id: bulkUploadResultId,
        },
      );

    if (b2bEmployeesBulkUploadResult.total_employees_error > 0) {
      const { data: b2bEmployeesBulkUploadErrors } =
        await dataProvider.getList<GetB2bEmployeesBulkUploadErrorsOneResponse>(
          `${resourceBasePath}employees/bulk_upload/errors`,
          {
            filter: {
              bulk_upload_result_id: bulkUploadResultId,
            },
            sort: { field: 'id', order: 'ASC' },
            pagination: {
              page: 1,
              perPage: 2000,
            },
          },
        );
      setBulkUploadErrors(b2bEmployeesBulkUploadErrors);
    }

    setBulkUploadResult(b2bEmployeesBulkUploadResult);
    if (b2bEmployeesBulkUploadResult.status !== 'finished') {
      setTimeout(getBulkUpload, REFRESH_TIMER);
    }
  }, [dataProvider, resourceBasePath, bulkUploadResultId, setBulkUploadResult]);

  if (G.isNullable(bulkUploadResult)) {
    getBulkUpload();
    return;
  }

  const {
    b2b_company_id: companyId,
    total_employees_affected: totalEmployeesAffected,
    total_employees_created: totalEmployeesCreated,
    total_employees_updated: totalEmployeesUpdated,
    total_employees_error: totalEmployeesError,
    status,
  } = bulkUploadResult;

  const statuses = computeStatuses(
    totalEmployeesAffected,
    totalEmployeesCreated,
    totalEmployeesUpdated,
    totalEmployeesError,
  );

  const hasError = totalEmployeesError > 0;
  const errorColor = hasError ? '#D32F2F' : '#AEAEAE';

  return (
    <>
      <DialogTitleWithX onClose={close}>
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
          {translate(
            'resources.bo_common.b2b.employees.actions.bulk_create.sections.results.title',
            {
              companyId,
            },
          )}
          <BulkUploadStatusBadge status={bulkUploadResult.status} />
        </Stack>
      </DialogTitleWithX>

      <DialogContent sx={{ maxHeight: '600px' }}>
        <Stack direction="column" spacing={2} paddingTop={2}>
          <Box>
            <Typography variant="body1">
              {translate(
                status === 'finished'
                  ? 'resources.bo_common.b2b.employees.actions.bulk_create.sections.results.finished'
                  : 'resources.bo_common.b2b.employees.actions.bulk_create.sections.results.subtitle',
              )}
            </Typography>
          </Box>
          <BulkUploadProgressBar statuses={statuses} />
          <BulkUploadStatusLegend statuses={statuses} />
          <Box width={'100%'}>
            <BulkUploadErrorsList
              hasError={hasError}
              errorColor={errorColor}
              errors={bulkUploadErrors}
              statuses={statuses}
            />
          </Box>
        </Stack>
      </DialogContent>
    </>
  );
}
